// PublicGuide — no-auth user guide rendered at /guide and /guide/:slug.
// Reads from /api/help/public/* endpoints. Articles are limited to those
// flagged is_public=true server-side.

import { useEffect, useState, useCallback } from "react";

type ArticleListItem = {
  slug: string;
  title: string;
  section: string;
  section_order: number;
  article_order: number;
};

type ArticleDetail = {
  slug: string;
  title: string;
  section: string;
  body_markdown: string;
  body_html: string;
  updated_at: string;
};

type SearchResult = {
  slug: string;
  title: string;
  section: string;
  rank: number;
  snippet: string;
};

const COLORS = {
  bg: "#F5F7FA",
  card: "#FFFFFF",
  border: "#DDE1E8",
  text: "#1A1F2E",
  textMuted: "#6B7280",
  accent: "#2B7A3E",
  accentLight: "#E6F3EA",
};

export const PublicGuide = ({ initialSlug }: { initialSlug?: string | null }) => {
  const [articles, setArticles] = useState<ArticleListItem[]>([]);
  const [activeSlug, setActiveSlug] = useState<string | null>(initialSlug || null);
  const [activeArticle, setActiveArticle] = useState<ArticleDetail | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/help/public/articles").then(r => r.json());
        if (cancelled) return;
        const list: ArticleListItem[] = r.articles || [];
        setArticles(list);
        setActiveSlug(prev => prev || (list.length > 0 ? list[0].slug : null));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!activeSlug) { setActiveArticle(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/help/public/articles/${encodeURIComponent(activeSlug)}`).then(r => r.json());
        if (!cancelled && r.article) setActiveArticle(r.article);
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, "", `/guide/${encodeURIComponent(activeSlug)}`);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [activeSlug]);

  useEffect(() => {
    if (searchQuery.trim().length < 2) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/help/public/search?q=${encodeURIComponent(searchQuery)}`).then(r => r.json());
        setSearchResults(r.results || []);
      } catch { setSearchResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const onSelectArticle = useCallback((slug: string) => {
    setActiveSlug(slug);
    setSearchQuery("");
    setSearchResults(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const sectionGroups = articles.reduce((acc, a) => {
    if (!acc[a.section]) acc[a.section] = [];
    acc[a.section].push(a);
    return acc;
  }, {} as Record<string, ArticleListItem[]>);

  return (
    <div data-deploy-marker="wave6-public-guide" style={{ minHeight: "100vh", backgroundColor: COLORS.bg, fontFamily: "'Outfit', 'Segoe UI', sans-serif", color: COLORS.text }}>
      <div style={{ backgroundColor: COLORS.accent, padding: "16px 28px", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: ".02em" }}>KontractAI User Guide</div>
        <a href="/" style={{ color: "#fff", fontSize: 12, textDecoration: "none", opacity: .9 }}>Sign in</a>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", maxWidth: 1100, margin: "0 auto", padding: "28px 20px", gap: 28 }}>
        <aside>
          <input
            type="search"
            placeholder="Search the guide..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.card, fontSize: 13, fontFamily: "inherit", marginBottom: 16, boxSizing: "border-box" }}
          />

          {searchResults !== null ? (
            <div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 8 }}>
                Search results ({searchResults.length})
              </div>
              {searchResults.length === 0 && <div style={{ fontSize: 12, color: COLORS.textMuted }}>No results.</div>}
              {searchResults.map(r => (
                <button key={r.slug} onClick={() => onSelectArticle(r.slug)} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 4, border: "none", backgroundColor: r.slug === activeSlug ? COLORS.accentLight : "transparent", color: COLORS.text, fontSize: 12, cursor: "pointer", marginBottom: 4, fontFamily: "inherit" }}>
                  <div style={{ fontWeight: 600 }}>{r.title}</div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }} dangerouslySetInnerHTML={{ __html: r.snippet }} />
                </button>
              ))}
            </div>
          ) : loading ? (
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>Loading...</div>
          ) : (
            Object.entries(sectionGroups).map(([section, items]) => (
              <div key={section} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6, fontWeight: 700 }}>{section}</div>
                {items.map(a => (
                  <button key={a.slug} onClick={() => onSelectArticle(a.slug)} style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", borderRadius: 4, border: "none", backgroundColor: a.slug === activeSlug ? COLORS.accentLight : "transparent", color: a.slug === activeSlug ? COLORS.accent : COLORS.text, fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: a.slug === activeSlug ? 600 : 400 }}>
                    {a.title}
                  </button>
                ))}
              </div>
            ))
          )}
        </aside>

        <main style={{ backgroundColor: COLORS.card, padding: 32, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
          {activeArticle ? (
            <article>
              <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 }}>{activeArticle.section}</div>
              <h1 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 16px", color: COLORS.text }}>{activeArticle.title}</h1>
              <div className="help-article-body" style={{ fontSize: 14, lineHeight: 1.7, color: COLORS.text }} dangerouslySetInnerHTML={{ __html: activeArticle.body_html }} />
            </article>
          ) : !loading && articles.length === 0 ? (
            <div style={{ fontSize: 14, color: COLORS.textMuted, textAlign: "center", padding: 40 }}>
              No public articles available yet.
            </div>
          ) : (
            <div style={{ fontSize: 14, color: COLORS.textMuted }}>Loading...</div>
          )}
        </main>
      </div>
    </div>
  );
};
